import React from "react";
import VariantGate from './_variant_gate';

import NavBar from "../components/common/nav_bar";
import ScrollToTopButton from "../components/common/scroll_to_top_button";
import ToastBanner from "../components/common/toast_banner";
import MaintenanceBanner from "../components/common/maintenance_banner";
import Skeleton from "../components/common/skeleton";
import FilterPill from "../components/common/filter_pill";
import EmptyState from "../components/common/empty_state";
import PositionTable from "../components/dashboard_page/position_table";
import BtcDcaPanel from "../components/dashboard_page/btc_dca_panel";
import StrategyOverviewCards from "../components/dashboard_page/strategy_overview_cards";
import TotalBalanceExchangeRateBase from "../components/dashboard_page/total_balance_exchange_rate_base";
import TotalInfoDescription from "../components/dashboard_page/total_info_description";
import { fetchBalance, fetchCurrentRatio, fetchExchangeRate, fetchBalanceHistory } from "../services/balanceApi";
import { getAccessToken, getStoredUsername, requireLoginOrThrow, clearLoginSession } from "../utils/auth";
import { getNavCollapsed, setNavCollapsed } from "../utils/navState";
import { wsMessageToState } from "../utils/dashboardWsMessage";
import { fiatPositionValueKRW } from "../utils/upbitPosition";
import { buildBtcDcaSummary } from "../utils/btcDca";
import { computeRebalance } from "../utils/rebalance";
import { getSeries as getBtcSeries } from "../services/btcApi";
import { buildDeltaSeries, buildDeltaMonthlySummary } from "../utils/deltaHistory";
import DeltaHistoryChart from "../components/dashboard_page/delta_history_chart";
import DeltaMonthlySummary from "../components/dashboard_page/delta_monthly_summary";
import HistoryTable from "../components/history_page/history_table";
import DashboardWebSocket from "../services/websocketClient";
import StrategyServiceStatusBoard from "../components/dashboard_page/strategy_service_status_board";
import { getServicesStatus } from "../services/strategyControlApi";
import {
    getSummary as getStockSummary,
    getHoldings as getStockHoldings,
    getSeries as getStockSeries,
    getPurchases as getStockPurchases,
    createPurchase as createStockPurchase,
    confirmPurchase as confirmStockPurchase,
    editPurchase as editStockPurchase,
    deletePurchase as deleteStockPurchase,
} from "../services/stockApi";
import StockSummaryHero from "../components/stocks/stock_summary_hero";
import PortfolioAllocation from "../components/stocks/portfolio_allocation";
import AccumulationChart from "../components/stocks/accumulation_chart";
import AccountCards from "../components/stocks/account_cards";
import FxBreakdownStrip from "../components/stocks/fx_breakdown_strip";
import HoldingsTable from "../components/stocks/holdings_table";
import PurchaseTimeline from "../components/stocks/purchase_timeline";
import PurchaseFormModal from "../components/stocks/purchase_form_modal";
import { showToast } from "../utils/show_toast";

// 미국주식 요약 재조회 주기 — 시세 캐시 TTL(20분) 대비 여유있게 5분 (DAS-164)
const STOCK_SUMMARY_POLL_MS = 5 * 60 * 1000;

// 대시보드 전략 뷰 localStorage 영속 키 — DAS-164 탭 통합 키를 계속 재사용한다.
const ACTIVE_TAB_KEY = "dashboard_activeTab";

// 전략 뷰 4종 (DAS-175 Option A) — 최상위 축이 자산군(크립토/미국주식)에서 전략으로 바뀌었다.
const VIEW_MODES = ["all", "btc", "delta", "stock"];
// DAS-171까지 저장된 값의 마이그레이션. "crypto"를 보던 사용자의 실질 관심은 리밸런싱이므로 델타전략으로,
// "stocks"는 이름만 바뀐 같은 화면이므로 그대로 옮긴다.
const LEGACY_VIEW_MODES = { crypto: "delta", stocks: "stock" };

const getStoredViewMode = () => {
    const stored = localStorage.getItem(ACTIVE_TAB_KEY);
    if (VIEW_MODES.includes(stored)) return stored;
    return LEGACY_VIEW_MODES[stored] || "all";
};

// 미국주식 상세 데이터가 필요한 뷰 — 전체 뷰는 요약(summary)만 쓰므로 상세 lazy fetch 대상이 아니다.
const needsStockDetail = (mode) => mode === "stock";

// 델타 히스토리 합산에서 제외할 전략 — DeltaFx 와 같은 계좌 키를 중복 보고한다 (DAS-175와 같은 가드)
const LISTING_SNIPE_NAME_HIST = "ListingSnipe";

class DashboardPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            collapsedSections: {},
            activeStrategyTab: null,
            loading: false,
            lastUpdated: null,
            exchangeRate: 0,
            // 환율 출처(DAS-181) — "koreaexim"(정상) | "upbit_usdt"(TLS 실패 등으로 김프 포함 시세 폴백).
            exchangeRateSource: null,
            balanceInfo: [],
            currentRatio: {},
            derivedTotals: {
                totalBalance: 0,
                totalBalanceBaseBTC: 0,
                totalRiskyBalance: 0,
                totalRiskyBalanceBaseBTC: 0,
                totalFiatBalance: 0,
            },
            prevDerivedTotals: null,
            sortedPairs: [],
            wsStatus: 'disconnected',
            wsReconnectAttempt: 0,
            isPollingFallback: false,
            excludeBTC: localStorage.getItem("dashboard_excludeBTC") !== "false",
            strategyServices: [],
            strategyDisconnected: false,
            stockSummary: null,
            stockSummaryError: false,
            viewMode: getStoredViewMode(),
            // BTC 적립 곡선 (DAS-177) — BTC 뷰 최초 진입 시 1회 lazy fetch
            btcSeriesPoints: [],
            btcSeriesLoading: true,
            btcSeriesError: false,
            btcSnapshotSince: null,
            // 델타 히스토리 (DAS-179, History 에서 이동) — 델타 뷰 최초 진입 시 1회 조회
            deltaHistoryRows: [],
            deltaHistoryLoading: true,
            deltaHistoryError: false,
            stockHoldings: [],
            stockHoldingsLoading: true,
            stockHoldingsError: false,
            stockSeriesPoints: [],
            stockSeriesLoading: true,
            stockSeriesError: false,
            stockSeriesSnapshotSince: null,
            stockSeriesAccountFallback: false,
            selectedAccountId: null, // 계좌 칩/카드 필터 (DAS-171 P1 E-5~E-7) — null=전체
            stockPurchases: [],
            stockPurchasesLoading: true,
            stockPurchasesError: false,
            stockPurchaseModalOpen: false,
            stockPurchaseModalMode: "create", // "create" | "edit" (DAS-169)
            stockPurchaseEditTarget: null,     // edit 모드 프리필 대상 원본 행 (DAS-169)
            stockPurchaseSaving: false,
        };

        this.dashboardWs = null;
        this.pollingInterval = null;
        this.keepAliveInterval = null;
        this.strategyPollingInterval = null;
        this.staleWatchdogInterval = null;
        this.stockSummaryPollingInterval = null;
        this.hiddenAt = null;
        // 미국주식 탭 데이터는 최초 활성화 시 1회만 lazy fetch (탭 전환 시 재요청 방지)
        this.stockTabDataLoaded = false;
        this.btcSeriesLoaded = false;
        this.deltaHistoryLoaded = false;
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        // iOS PWA (standalone) 모드 감지 — 진단용 1회 로그
        const isStandalone =
            (typeof window.matchMedia === 'function' &&
                window.matchMedia('(display-mode: standalone)').matches) ||
            window.navigator.standalone === true;
        if (isStandalone) {
            console.log('[Dashboard] PWA standalone mode detected');
        }

        this.fetchData();
        this.connectWebSocket();
        this.keepAliveInterval = setInterval(() => {
            if (this.dashboardWs && this.dashboardWs.isConnected()) {
                this.dashboardWs.ping();
            }
        }, 30000);
        // Staleness watchdog: 15초 간격으로 메시지 미수신 임계값 확인.
        // iOS 는 백그라운드에서 setInterval 정지 → 이 루프는 포그라운드 복구 직후부터 재개된다.
        this.staleWatchdogInterval = setInterval(() => {
            if (this.dashboardWs && this.dashboardWs.isStale()) {
                console.log('[WS] stale connection, force reconnecting');
                this.dashboardWs.forceReconnect({ force: true });
            }
        }, 15000);
        this._pollStrategy();
        this.strategyPollingInterval = setInterval(this._pollStrategy, 5000);

        // 미국주식 요약 (DAS-164) — 대시보드 WS/폴링과 별개로 5분 간격 조회
        this.fetchStockSummary();
        this.stockSummaryPollingInterval = setInterval(this.fetchStockSummary, STOCK_SUMMARY_POLL_MS);
        // 새로고침 시 저장된 뷰가 미국주식 상세면 그 데이터도 즉시 로드 (DAS-171 F-2 → DAS-175에서 뷰 이름만 변경)
        if (needsStockDetail(this.state.viewMode)) {
            this.loadStockTabData();
        }
        if (this.state.viewMode === "btc") {
            this.loadBtcSeries();
        }
        if (this.state.viewMode === "delta") {
            this.loadDeltaHistory();
        }

        // iOS PWA 백그라운드 → 포그라운드 복귀 시 WebSocket 재연결 트리거
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        window.addEventListener('pageshow', this.handlePageShow);
        window.addEventListener('online', this.handleOnline);
    }

    componentWillUnmount() {
        this.stopPolling();
        if (this.dashboardWs) {
            this.dashboardWs.disconnect();
            this.dashboardWs = null;
        }
        if (this.keepAliveInterval) {
            clearInterval(this.keepAliveInterval);
            this.keepAliveInterval = null;
        }
        if (this.staleWatchdogInterval) {
            clearInterval(this.staleWatchdogInterval);
            this.staleWatchdogInterval = null;
        }
        if (this.strategyPollingInterval) {
            clearInterval(this.strategyPollingInterval);
            this.strategyPollingInterval = null;
        }
        if (this.stockSummaryPollingInterval) {
            clearInterval(this.stockSummaryPollingInterval);
            this.stockSummaryPollingInterval = null;
        }
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        window.removeEventListener('pageshow', this.handlePageShow);
        window.removeEventListener('online', this.handleOnline);
    }

    handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            this.hiddenAt = Date.now();
        } else if (document.visibilityState === 'visible') {
            const hiddenFor = this.hiddenAt ? Date.now() - this.hiddenAt : 0;
            this.hiddenAt = null;
            // 숨김 기간이 5초 이상이거나 소켓이 stale 하면 강제 재연결 (zombie 복구)
            const stale =
                (this.dashboardWs && this.dashboardWs.isStale && this.dashboardWs.isStale()) || false;
            const force = hiddenFor > 5000 || stale;
            this._wakeUpConnection(force);
        }
    };

    handlePageShow = (e) => {
        // bfcache 복원 또는 PWA relaunch — 항상 강제 재연결
        this._wakeUpConnection(true);
    };

    handleOnline = () => {
        // 네트워크 복구 — 항상 강제 재연결
        this._wakeUpConnection(true);
    };

    _wakeUpConnection = (force = false) => {
        if (this.dashboardWs) {
            this.dashboardWs.forceReconnect({ force });
        } else {
            this.connectWebSocket();
        }
        this.fetchData();
    };

    // 전략 서비스 상태 보드용 폴링. 예약(schedule) 조회는 ListingSnipe 전용이라 DAS-175에서 제거했다.
    _pollStrategy = async () => {
        try {
            const statusData = await getServicesStatus();
            this.setState({
                strategyServices: Array.isArray(statusData?.services) ? statusData.services : [],
                strategyDisconnected: false,
            });
        } catch (_err) {
            this.setState({ strategyDisconnected: true });
        }
    };

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.exchangeRate !== this.state.exchangeRate ||
            prevState.balanceInfo !== this.state.balanceInfo ||
            prevState.currentRatio !== this.state.currentRatio
        ) {
            const { totals, sortedPairs } = this.buildDashboardData(
                this.state.balanceInfo,
                this.state.exchangeRate,
                this.state.currentRatio
            );
            this.setState({
                prevDerivedTotals: this.state.derivedTotals,
                derivedTotals: totals,
                sortedPairs,
            });
        }

        // 계좌 칩/카드 필터 고아 선택 가드 (P1 검수 M-5) — 선택 유지는 두 소스(holdingAccounts=칩,
        // stockSummary.accounts=카드) 중 하나에만 살아있어도 된다: 카드로 고른 예수금 전용 계좌는
        // holdingAccounts엔 절대 없지만 카드가 살아있는 한 선택을 지워선 안 된다(일관된 UX). 탈출
        // 불가 함정(M-5 본래 문제)은 두 소스 모두에서 사라진 경우에만 발생하므로, 그때만 null로 되돌린다.
        if (
            (prevState.stockHoldings !== this.state.stockHoldings ||
                prevState.stockSummary !== this.state.stockSummary) &&
            this.state.selectedAccountId != null
        ) {
            const holdingAccounts = this.computeHoldingAccounts(this.state.stockHoldings);
            const inHoldingChips = holdingAccounts.some((a) => a.account_id === this.state.selectedAccountId);
            const summaryAccounts = Array.isArray(this.state.stockSummary?.accounts)
                ? this.state.stockSummary.accounts
                : [];
            const inSummaryCards = summaryAccounts.some((a) => a.account_id === this.state.selectedAccountId);
            if (!inHoldingChips && !inSummaryCards) {
                this.onSelectAccount(null);
            }
        }
    }

    connectWebSocket = () => {
        const username = getStoredUsername();
        if (!username || !getAccessToken()) {
            this.startPolling();
            return;
        }
        this.dashboardWs = new DashboardWebSocket(
            username,
            getAccessToken,
            this.handleWsMessage,
            this.handleWsStatusChange,
            this.handleWsAuthFailure
        );
        this.dashboardWs.connect();
    };

    handleWsMessage = (message) => {
        if (!message) return;
        // 서버 envelope { type, data } 를 state 패치로 변환(DAS-144). 과거엔 중첩
        // message.data.data_type 을 읽어 모든 push 가 무시돼 값이 갱신되지 않았다.
        this.setState({ ...wsMessageToState(message), lastUpdated: new Date() });
    };

    handleWsAuthFailure = () => {
        try { clearLoginSession(); } catch (e) {}
        window.location.replace("/login");
    };

    handleWsStatusChange = (status, reconnectAttempt) => {
        if (status === 'connected') {
            this.stopPolling();
            this.setState({ wsStatus: status, wsReconnectAttempt: 0, isPollingFallback: false });
        } else if (status === 'disconnected') {
            // 4001 인증 실패 등 영구 실패 경로 — 폴링으로 전환
            this.startPolling();
            this.setState({ wsStatus: status, wsReconnectAttempt: 0, isPollingFallback: true });
        } else {
            // reconnecting/connecting — 재시도가 3회를 넘기면 폴링을 safety net 으로 병행 시작
            const attempts = reconnectAttempt || 0;
            if (attempts >= 3 && !this.pollingInterval) {
                this.startPolling();
            }
            this.setState({
                wsStatus: status,
                wsReconnectAttempt: attempts,
                isPollingFallback: attempts >= 3,
            });
        }
    };

    startPolling = () => {
        if (this.pollingInterval) return;
        this.pollingInterval = setInterval(() => { this.fetchData(); }, 60000);
    };

    stopPolling = () => {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    };

    fetchData = () => {
        const username = getStoredUsername();
        if (!username) return;
        this.setState({ loading: true });
        Promise.allSettled([
            fetchExchangeRate(),
            fetchBalance(username),
            fetchCurrentRatio(username),
        ]).then(([rateResult, balanceResult, ratioResult]) => {
            const nextState = { loading: false, lastUpdated: new Date() };
            if (rateResult.status === "fulfilled") {
                nextState.exchangeRate = rateResult.value?.data?.exchange_rate ?? 0;
                const source = rateResult.value?.data?.source;
                if (typeof source === "string") nextState.exchangeRateSource = source;
            }
            if (balanceResult.status === "fulfilled") {
                nextState.balanceInfo = Array.isArray(balanceResult.value?.data) ? balanceResult.value.data : [];
            }
            if (ratioResult.status === "fulfilled") {
                const ratioData = ratioResult.value?.data;
                // 캐시 미스 시 서버가 빈 객체({})를 반환한다(DAS-181) — 기존 게이지 값을 지우지 않고
                // 유지해야 밴드 계산이 일시 실패해도 화면에서 게이지가 통째로 사라지지 않는다.
                if (ratioData && typeof ratioData === "object" && Object.keys(ratioData).length > 0) {
                    nextState.currentRatio = ratioData;
                }
            }
            this.setState(nextState);
        });
    };

    // 미국주식 요약 조회 — 실패 시 stockSummaryError로 표면화(Hero가 SectionError+재시도 렌더)
    fetchStockSummary = () => {
        getStockSummary()
            .then((res) => {
                const data = res?.data;
                const nextState = { stockSummaryError: false };
                if (data && typeof data === "object") {
                    nextState.stockSummary = data;
                }
                this.setState(nextState);
            })
            .catch(() => this.setState({ stockSummaryError: true }));
    };

    fetchStockHoldings = () => {
        this.setState({ stockHoldingsLoading: true, stockHoldingsError: false });
        getStockHoldings()
            .then((res) => this.setState({ stockHoldings: res?.data?.holdings || [], stockHoldingsLoading: false }))
            .catch(() => this.setState({ stockHoldingsLoading: false, stockHoldingsError: true }));
    };

    // 계좌 칩 필터용 — holdings에 실제로 등장하는 계좌만 중복 제거해 모은다 (P1-E5).
    // componentDidUpdate의 고아 선택 가드(M-5)와 render()가 공유한다.
    computeHoldingAccounts = (stockHoldings) => {
        const map = new Map();
        stockHoldings.forEach((h) => {
            (Array.isArray(h.accounts) ? h.accounts : []).forEach((a) => {
                if (!map.has(a.account_id)) map.set(a.account_id, a);
            });
        });
        return Array.from(map.values());
    };

    // 기간 토글은 accumulation_chart 내부에서 클라이언트 슬라이스로 처리하므로 전체 이력을 한 번만 조회한다.
    // accountId를 주면 그 계좌의 개별 시리즈를 조회한다(P1-E7). 계좌별 응답이 비면(강도 모드 또는
    // snapshot_since 이전) 전체 합계로 폴백하고 accumulation_chart가 안내 배너를 띄울 수 있게 플래그를 남긴다.
    fetchStockSeries = (accountId = this.state.selectedAccountId) => {
        this.setState({ stockSeriesLoading: true, stockSeriesError: false });
        getStockSeries("all", accountId || undefined)
            .then((res) => {
                const points = res?.data?.points || [];
                if (accountId && points.length === 0) {
                    this.setState({ stockSeriesAccountFallback: true, stockSeriesSnapshotSince: null });
                    getStockSeries("all")
                        .then((res2) =>
                            this.setState({ stockSeriesPoints: res2?.data?.points || [], stockSeriesLoading: false })
                        )
                        .catch(() => this.setState({ stockSeriesLoading: false, stockSeriesError: true }));
                    return;
                }
                this.setState({
                    stockSeriesPoints: points,
                    stockSeriesAccountFallback: false,
                    stockSeriesSnapshotSince: accountId ? res?.data?.snapshot_since || null : null,
                    stockSeriesLoading: false,
                });
            })
            .catch(() => this.setState({ stockSeriesLoading: false, stockSeriesError: true }));
    };

    // BTC 적립 곡선 — BTC 뷰에서만 필요하므로 최초 진입 시 1회 조회한다 (DAS-177).
    // 재시도는 loadBtcSeries 가 아니라 이 메서드를 직접 부른다(플래그를 우회해야 하므로).
    fetchBtcSeries = () => {
        this.setState({ btcSeriesLoading: true, btcSeriesError: false });
        getBtcSeries("all")
            .then((res) =>
                this.setState({
                    btcSeriesPoints: res?.data?.points || [],
                    btcSnapshotSince: res?.data?.snapshot_since || null,
                    btcSeriesLoading: false,
                })
            )
            .catch(() => this.setState({ btcSeriesLoading: false, btcSeriesError: true }));
    };

    loadBtcSeries = () => {
        if (this.btcSeriesLoaded) return;
        this.btcSeriesLoaded = true;
        this.fetchBtcSeries();
    };

    // 델타 히스토리 — 잔고 이력과 BTC 스냅샷을 함께 받아 BTC 적립분을 뺀다 (DAS-179).
    // 두 소스가 다 있어야 경계를 그릴 수 있으므로 한 번에 받고, 하나라도 실패하면 카드가 에러를 알린다.
    fetchDeltaHistory = () => {
        const username = getStoredUsername();
        if (!username) return;
        this.setState({ deltaHistoryLoading: true, deltaHistoryError: false });
        Promise.all([fetchBalanceHistory(username), getBtcSeries("all")])
            .then(([histRes, btcRes]) => {
                this.setState({
                    deltaHistoryRows: Array.isArray(histRes?.data) ? histRes.data : [],
                    btcSeriesPoints: btcRes?.data?.points || [],
                    btcSnapshotSince: btcRes?.data?.snapshot_since || null,
                    deltaHistoryLoading: false,
                });
            })
            .catch(() => this.setState({ deltaHistoryLoading: false, deltaHistoryError: true }));
    };

    loadDeltaHistory = () => {
        if (this.deltaHistoryLoaded) return;
        this.deltaHistoryLoaded = true;
        this.fetchDeltaHistory();
    };

    // 계좌 카드에서 별칭을 바꾼 뒤 — 라벨은 summary/holdings 양쪽에 실려 오므로 둘 다 다시 받아
    // 카드·칩·보유 표가 한 번에 새 이름으로 맞춰지게 한다 (DAS-175).
    handleAccountRenamed = () => {
        this.fetchStockSummary();
        this.fetchStockHoldings();
    };

    // 계좌 칩/카드 클릭 — 선택 상태를 바꾸고 그 계좌 기준으로 시리즈를 재조회한다 (P1-E5~E7).
    onSelectAccount = (accountId) => {
        this.setState({ selectedAccountId: accountId }, () => this.fetchStockSeries(accountId));
    };

    fetchStockPurchases = () => {
        this.setState({ stockPurchasesLoading: true, stockPurchasesError: false });
        getStockPurchases({ limit: 50 })
            .then((res) => this.setState({ stockPurchases: res?.data?.purchases || [], stockPurchasesLoading: false }))
            .catch(() => this.setState({ stockPurchasesLoading: false, stockPurchasesError: true }));
    };

    // 미국주식 탭 상세 데이터(holdings/series/purchases) 최초 1회 lazy fetch. summary는 기존 폴링 state 재사용.
    loadStockTabData = () => {
        if (this.stockTabDataLoaded) return;
        this.stockTabDataLoaded = true;
        this.fetchStockHoldings();
        this.fetchStockSeries();
        this.fetchStockPurchases();
    };

    openAddStockPurchaseModal = () =>
        this.setState({ stockPurchaseModalOpen: true, stockPurchaseModalMode: "create", stockPurchaseEditTarget: null });

    // 확정 행 수정 진입점 — 원본 행을 폼 필드 형태로 프리필한다 (DAS-169 T11).
    openEditStockPurchaseModal = (purchase) => {
        this.setState({
            stockPurchaseModalOpen: true,
            stockPurchaseModalMode: "edit",
            stockPurchaseEditTarget: {
                id: purchase.id,
                plan_id: purchase.plan_id ?? null,
                broker: purchase.broker || "kiwoom",
                ticker: purchase.ticker || "",
                name: purchase.name || "",
                trade_date: purchase.trade_date || "",
                quantity: purchase.quantity != null ? String(purchase.quantity) : "",
                price_usd: purchase.price_usd != null ? String(purchase.price_usd) : "",
                amount_krw: purchase.amount_krw != null ? String(purchase.amount_krw) : "",
                usd_krw: purchase.usd_krw != null ? String(purchase.usd_krw) : "",
            },
        });
    };

    closeStockPurchaseModal = () =>
        this.setState({ stockPurchaseModalOpen: false, stockPurchaseModalMode: "create", stockPurchaseEditTarget: null });

    // 등록/수정 공통 성공 처리 — holdings·summary·series·purchases 4종을 함께 재조회한다 (/stocks 흡수, DAS-164).
    handleStockPurchaseSubmit = (payload) => {
        const { stockPurchaseModalMode, stockPurchaseEditTarget } = this.state;
        const isEdit = stockPurchaseModalMode === "edit";
        this.setState({ stockPurchaseSaving: true });
        const request = isEdit
            ? editStockPurchase(stockPurchaseEditTarget?.id, payload)
            : createStockPurchase(payload);
        request
            .then(() => {
                showToast({ type: "success", message: isEdit ? "매수 기록을 수정했습니다." : "매수 기록을 등록했습니다." });
                this.setState({
                    stockPurchaseModalOpen: false,
                    stockPurchaseModalMode: "create",
                    stockPurchaseEditTarget: null,
                    stockPurchaseSaving: false,
                });
                this.fetchStockPurchases();
                this.fetchStockHoldings();
                this.fetchStockSummary();
                this.fetchStockSeries();
            })
            .catch((err) => {
                console.error("[Dashboard] stock purchase 저장 실패:", err?.response?.status, err?.response?.data);
                const msg = err?.response?.data?.message || (isEdit ? "매수 기록 수정에 실패했습니다." : "매수 기록 등록에 실패했습니다.");
                showToast({ type: "error", message: msg });
                this.setState({ stockPurchaseSaving: false });
            });
    };

    // 확정/건너뜀 처리 후 holdings·summary·series가 전부 파생 영향을 받으므로 함께 재조회한다.
    handleConfirmStockPurchase = (id, payload) => {
        return confirmStockPurchase(id, payload)
            .then(() => {
                showToast({
                    type: "success",
                    message: payload.action === "skip" ? "매수를 건너뛰었습니다." : "체결을 확정했습니다.",
                });
                this.fetchStockPurchases();
                this.fetchStockHoldings();
                this.fetchStockSummary();
                this.fetchStockSeries();
            })
            .catch((err) => {
                console.error("[Dashboard] stock purchase 처리 실패:", err?.response?.status, err?.response?.data);
                const msg = err?.response?.data?.message || "처리에 실패했습니다.";
                showToast({ type: "error", message: msg });
                throw err;
            });
    };

    // 확정 행 삭제 — 성공 시 holdings·summary·series·purchases 4종 재조회.
    handleDeleteStockPurchase = (id) => {
        return deleteStockPurchase(id)
            .then(() => {
                showToast({ type: "success", message: "매수 기록을 삭제했습니다." });
                this.fetchStockPurchases();
                this.fetchStockHoldings();
                this.fetchStockSummary();
                this.fetchStockSeries();
            })
            .catch((err) => {
                console.error("[Dashboard] stock purchase 삭제 실패:", err?.response?.status, err?.response?.data);
                const msg = err?.response?.data?.message || "매수 기록 삭제에 실패했습니다.";
                showToast({ type: "error", message: msg });
                throw err;
            });
    };

    // 전략 뷰 전환 — all|btc|delta|stock (DAS-175 Option A).
    setViewMode = (mode) => {
        if (mode === this.state.viewMode) return;
        localStorage.setItem(ACTIVE_TAB_KEY, mode);
        this.setState({ viewMode: mode });
        if (needsStockDetail(mode)) {
            this.loadStockTabData();
        }
        if (mode === "btc") {
            this.loadBtcSeries();
        }
        if (mode === "delta") {
            this.loadDeltaHistory();
        }
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    toggleExcludeBTC = () => {
        this.setState((prev) => {
            const next = !prev.excludeBTC;
            localStorage.setItem("dashboard_excludeBTC", String(next));
            return { excludeBTC: next };
        });
    };

    getMidRatio = (value) => {
        if (Number.isFinite(value)) return value;
        if (value && typeof value === "object") {
            const middle = Number(value.middle ?? value.mid ?? value.current ?? value.value);
            return Number.isFinite(middle) ? middle : Number.NaN;
        }
        return Number.NaN;
    };

    buildDashboardData = (balanceInfo, exchangeRate, currentRatio) => {
        const safeBalanceInfo = Array.isArray(balanceInfo) ? balanceInfo : [];
        const totals = {
            totalBalance: 0, totalBalanceBaseBTC: 0,
            totalRiskyBalance: 0, totalRiskyBalanceBaseBTC: 0, totalFiatBalance: 0,
            totalBtcSpotBalance: 0, // 보유 BTC 현물 평가액 합 — 리밸런싱 계산 차감용 (DAS-172)
        };
        // ListingSnipe는 DeltaFx와 같은 계좌 키를 중복 보고하므로 총계에서 뺀다 (DAS-175에서 UI는 제거, 이 가드는 유지)
        const LISTING_SNIPE = "ListingSnipe";

        safeBalanceInfo.forEach((info) => {
            const strategyName = info?.strategy_name || "default";
            // ListingSnipe 전략 자체는 총 잔고에서 제외 (같은 키 중복)
            if (strategyName === LISTING_SNIPE) return;
            const fiatExchange = info?.fiat_exchange || "none";
            const riskyExchange = info?.risky_exchange || "none";
            const indexKey = `${strategyName}:${fiatExchange}-${riskyExchange}`;
            let tmpFiatBalance = parseFloat(info?.fiat_balance?.total_fiat_balance) || 0;
            let tmpRiskyBalance = parseFloat(info?.risky_balance?.total_margin_balance) || 0;

            const tmpCurrentRatio = this.getMidRatio(currentRatio[indexKey]);
            const numericCurrentRatio = Number.isFinite(tmpCurrentRatio) ? tmpCurrentRatio : 0;
            totals.totalBalance += tmpFiatBalance + exchangeRate * tmpRiskyBalance;
            totals.totalBalanceBaseBTC += tmpFiatBalance + numericCurrentRatio * tmpRiskyBalance;
            totals.totalRiskyBalance += exchangeRate * tmpRiskyBalance;
            totals.totalRiskyBalanceBaseBTC += numericCurrentRatio * tmpRiskyBalance;
            totals.totalFiatBalance += tmpFiatBalance;
            // 보유 BTC 현물 평가액 누적 (DAS-172) — BTC 적립/델타전략을 가르는 경계값 (DAS-175)
            (info?.fiat_balance?.positions || []).forEach((p) => {
                if ((p.currency || "").toUpperCase() === "BTC") {
                    totals.totalBtcSpotBalance += fiatPositionValueKRW(p);
                }
            });
        });
        const sortedPairs = safeBalanceInfo
            .filter((info) => (info?.strategy_name || "default") !== LISTING_SNIPE)
            .sort((a, b) => {
                const aFiat = a?.fiat_balance?.total_fiat_balance || 0;
                const aRisky = a?.risky_balance?.total_margin_balance || 0;
                const bFiat = b?.fiat_balance?.total_fiat_balance || 0;
                const bRisky = b?.risky_balance?.total_margin_balance || 0;
                return (bFiat + exchangeRate * bRisky) - (aFiat + exchangeRate * aRisky);
            })
            .map((info) => {
                const strategyName = info?.strategy_name || "default";
                const fiatExchange = info?.fiat_exchange || "none";
                const riskyExchange = info?.risky_exchange || "none";
                const indexKey = `${strategyName}:${fiatExchange}-${riskyExchange}`;
                return { key: indexKey, strategyName, fiatExchange, riskyExchange, indexKey, info };
            });
        return { totals, sortedPairs };
    };

    formatLastUpdated = (date) => {
        if (!date) return "";
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        return `${hours}:${minutes}:${seconds}`;
    };

    render() {
        const {
            collapsed, loading, lastUpdated, exchangeRate, currentRatio,
            collapsedSections, derivedTotals, sortedPairs, wsStatus, wsReconnectAttempt, isPollingFallback,
            excludeBTC, balanceInfo, viewMode, stockSummary, stockSummaryError,
            stockHoldings, stockHoldingsLoading, stockHoldingsError,
            stockSeriesPoints, stockSeriesLoading, stockSeriesError,
            stockSeriesSnapshotSince, stockSeriesAccountFallback, selectedAccountId,
            stockPurchases, stockPurchasesLoading, stockPurchasesError,
            stockPurchaseModalOpen, stockPurchaseModalMode, stockPurchaseEditTarget, stockPurchaseSaving,
        } = this.state;

        // 미국주식 탭 전부 빈 데이터 여부 — 로드 완료 + 에러 없음 + holdings/purchases/summary 전부 비어있을 때만 빈 상태 표시
        const stockTabLoaded = !stockHoldingsLoading && !stockPurchasesLoading;
        // stockSummary 실패는 stale 데이터가 남아있으면(웜 실패, 예: 5분 폴링 1회 실패) 탭 에러로 취급하지 않는다 —
        // 콜드 실패(데이터 자체가 없음)일 때만 섹션 EmptyState를 막고 SectionError가 렌더될 여지를 남긴다.
        const stockSummaryColdError = stockSummaryError && !stockSummary;
        const stockTabHasError = stockHoldingsError || stockSeriesError || stockPurchasesError || stockSummaryColdError;
        const stockTabEmpty =
            stockTabLoaded &&
            !stockTabHasError &&
            stockHoldings.length === 0 &&
            stockPurchases.length === 0 &&
            !(stockSummary && Number(stockSummary.total_value_krw) > 0);

        // 계좌 칩 필터용 — holdings에 실제로 등장하는 계좌만 중복 제거해 모은다 (P1-E5).
        // summary.accounts(카드 스트립, P1-E6)와 소스가 다르다: 보유 종목이 없는(예수금만 있는)
        // 계좌는 카드에는 뜨지만 칩에는 뜨지 않는다 — 칩은 holdings 필터링에만 의미가 있다.
        const holdingAccounts = this.computeHoldingAccounts(stockHoldings);
        const showAccountChips = holdingAccounts.length >= 3;
        const filteredStockHoldings = selectedAccountId
            ? stockHoldings.filter(
                (h) => Array.isArray(h.accounts) && h.accounts.some((a) => a.account_id === selectedAccountId)
            )
            : stockHoldings;

        const sections = sortedPairs.map((entry) => {
            const info = entry.info;
            const fiatExchange = entry.fiatExchange;
            const riskyExchange = entry.riskyExchange;
            const rawFiatPositions = info?.fiat_balance?.positions || [];
            const rawRiskyPositions = info?.risky_balance?.positions || [];

            // 보유 BTC 현물 평가액 — 리밸런싱(보내야 할 금액) 계산에서 차감할 값 (DAS-172).
            // excludeBTC 토글(손익 표시용)과 무관하게 실보유 기준으로 항상 집계한다.
            const btcSpotBalance = rawFiatPositions
                .filter((p) => (p.currency || "").toUpperCase() === "BTC")
                .reduce((sum, p) => sum + fiatPositionValueKRW(p), 0);

            // BTC 제외 필터링
            const fiatPositions = excludeBTC
                ? rawFiatPositions.filter((p) => (p.currency || "").toUpperCase() !== "BTC")
                : rawFiatPositions;
            const riskyPositions = excludeBTC
                ? rawRiskyPositions.filter((p) => !(p.symbol || "").toUpperCase().startsWith("BTC"))
                : rawRiskyPositions;

            // BTC P&L 차감: API의 사전 집계 총액에서 BTC 개별 P&L을 빼기
            let fiatTotalUnrealizedProfit = parseFloat(info?.fiat_balance?.total_unrealized_profit) || 0;
            let riskyTotalUnrealizedProfit = parseFloat(info?.risky_balance?.total_unrealized_profit) || 0;

            if (excludeBTC) {
                const allFiat = info?.fiat_balance?.positions || [];
                const allRisky = info?.risky_balance?.positions || [];
                const btcFiat = allFiat.find((p) => (p.currency || "").toUpperCase() === "BTC");
                const btcRisky = allRisky.find((p) => (p.symbol || "").toUpperCase().startsWith("BTC"));
                if (btcFiat) fiatTotalUnrealizedProfit -= (btcFiat.profit_loss_amount || 0);
                if (btcRisky) riskyTotalUnrealizedProfit -= (parseFloat(btcRisky.unrealizedProfit) || 0);
            }

            const fiatTotalBalance = parseFloat(info?.fiat_balance?.total_fiat_balance) || 0;
            const riskyTotalMarginBalance = parseFloat(info?.risky_balance?.total_margin_balance) || 0;
            const totalBalance = fiatTotalBalance + exchangeRate * riskyTotalMarginBalance;
            const label = fiatExchange !== "none" && riskyExchange !== "none"
                ? `${fiatExchange}-${riskyExchange}`
                : fiatExchange !== "none" ? `${fiatExchange} (현물)` : `${riskyExchange} (선물)`;
            // 잔고 조회 실패 가시화(DAS-181) — facade가 거래소 실패 시 직전 값을 stale=true로 병합해 내려준다.
            const fetchErrors = [info?.risky_error, info?.fiat_error].filter(
                (e) => typeof e === "string" && e.length > 0
            );
            // 배지 우선순위: 직전 값 유지 중 → "조회 지연", 에러가 있는데 잔고가 전혀 없음 → "조회 실패",
            // 한쪽만 실패해 직전 값도 없이 빠짐 → "일부 조회 실패" (합계에서 그 쪽이 조용히 빠지는 것을 표면화)
            const hasBalance = Boolean(info?.risky_balance || info?.fiat_balance || info?.risky_spot_balance);
            let fetchBadge = null;
            if (info?.stale) fetchBadge = "조회 지연";
            else if (fetchErrors.length > 0) fetchBadge = hasBalance ? "일부 조회 실패" : "조회 실패";
            return {
                key: entry.key, label, strategyName: entry.strategyName, totalBalance,
                fiatTotalBalance, fiatTotalUnrealizedProfit, riskyTotalMarginBalance,
                riskyTotalUnrealizedProfit, riskyPositions, fiatPositions, btcSpotBalance,
                indexKey: entry.indexKey, markPrices: info?.risky_balance?.mark_prices,
                stale: Boolean(info?.stale), fetchErrors, fetchedAt: info?.fetched_at || null,
                fetchBadge, hasBalance,
                // DAS-182: 업비트 코인빌리기(담보·대여) 잔차 추정 — 없으면 null(카드/블록 미표시)
                loan: info?.fiat_balance?.loan || null,
            };
        });

        // ── 전략 3분할 파생값 (DAS-175 Option A) ──
        // BTC 적립과 델타전략은 같은 업비트 계좌라서 계좌로는 못 나눈다. 경계는 보유 BTC 현물 평가액이다.
        const btcSummary = buildBtcDcaSummary(balanceInfo, { excludeStrategies: ["ListingSnipe"] });
        const deltaMetrics = computeRebalance({
            totalBalance: derivedTotals.totalBalance,
            totalFiatBalance: derivedTotals.totalFiatBalance,
            totalRiskyBalance: derivedTotals.totalRiskyBalance,
            btcSpotBalance: derivedTotals.totalBtcSpotBalance,
        });

        const heroSkeleton = (
            <div className="space-y-3">
                <Skeleton variant="card" height="100px" />
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} variant="card" height="72px" />
                    ))}
                </div>
            </div>
        );
        const heroLoading = loading && balanceInfo.length === 0;
        // 전체/델타 두 뷰가 같은 Hero 컴포넌트를 variant만 바꿔 쓴다 — 값 산식이 갈라지지 않게 하기 위함.
        const heroBlock = (variant) =>
            heroLoading ? (
                heroSkeleton
            ) : (
                <TotalInfoDescription
                    variant={variant}
                    totalBalance={derivedTotals.totalBalance}
                    totalBalanceBaseBTC={derivedTotals.totalBalanceBaseBTC}
                    totalRiskyBalance={derivedTotals.totalRiskyBalance}
                    totalRiskyBalanceBaseBTC={derivedTotals.totalRiskyBalanceBaseBTC}
                    totalFiatBalance={derivedTotals.totalFiatBalance}
                    btcSpotBalance={derivedTotals.totalBtcSpotBalance}
                    exchangeRate={exchangeRate}
                    prevTotalBalance={this.state.prevDerivedTotals?.totalBalance}
                    stockSummary={this.state.stockSummary}
                    onStockCardClick={() => this.setViewMode('stock')}
                />
            );

        // ── 델타 히스토리 파생 (DAS-179) ──
        // 잔고 이력은 전략별 배열의 배열이다. 그냥 펼쳐서 이으면 DeltaFx 행(₩1억대)과 잔고가 비어 있는
        // 전략 행(수천 원)이 번갈아 찍혀 톱니가 된다 — 같은 시각끼리 합산해야 한다 (DAS-180).
        const deltaSeries = buildDeltaSeries(this.state.deltaHistoryRows, this.state.btcSeriesPoints, {
            excludeStrategies: [LISTING_SNIPE_NAME_HIST],
        });
        const deltaMonthly = buildDeltaMonthlySummary(deltaSeries.points);
        // HistoryTable 은 원본 행 모양(created/fiat_balance/risky_balance_krw/total_balance/exchange_rate)을
        // 기대하므로, BTC 를 뺀 값으로 그 모양을 다시 만들어 넘긴다 — 최신이 위로 오게 뒤집는다.
        const deltaTableRows = deltaSeries.points
            .slice()
            .reverse()
            .map((p) => ({
                created: new Date(p.time),
                fiat_balance: p.fiat,
                risky_balance_krw: p.risky,
                total_balance: p.total,
                exchange_rate: p.rate,
            }));

        // 연결 상태·갱신 시각·새로고침 — 종전에는 크립토 카드 안에만 있어서 다른 뷰에서는 보이지 않았다.
        // 뷰가 4개로 늘어난 만큼 어느 뷰에서나 보이도록 세그먼트 옆으로 끌어올린다 (DAS-175).
        const wsLabel =
            wsStatus === 'connected'
                ? '실시간 연결'
                : (wsStatus === 'connecting' || wsStatus === 'reconnecting')
                  ? '연결 중...'
                  : isPollingFallback ? '폴링 모드 (60초)' : '연결 끊김';
        const statusBar = (
            <div className="flex shrink-0 items-center gap-2">
                <span
                    role="status"
                    aria-label={wsLabel}
                    title={wsLabel}
                    className={`inline-block h-2 w-2 rounded-full ${
                        wsStatus === 'connected'
                            ? 'bg-gain'
                            : (wsStatus === 'connecting' || wsStatus === 'reconnecting')
                              ? 'bg-warning animate-pulse-soft'
                              : isPollingFallback
                                ? 'bg-brand'
                                : 'bg-content-muted'
                    }`}
                />
                <span className="text-xs text-content-muted">
                    {wsStatus === 'connected' ? '실시간'
                        : wsStatus === 'reconnecting' ? `재연결 중 (${wsReconnectAttempt})`
                        : wsStatus === 'connecting' ? '연결 중...'
                        : isPollingFallback ? '폴링' : '끊김'}
                </span>
                {lastUpdated && (
                    <span className="hidden font-num text-xs text-content-muted sm:inline">
                        {this.formatLastUpdated(lastUpdated)} 갱신
                    </span>
                )}
                <button
                    type="button"
                    onClick={this.fetchData}
                    disabled={loading}
                    aria-label="데이터 새로고침"
                    className="flex min-h-[44px] items-center gap-1 rounded-lg border border-surface-border px-2 py-1 text-xs font-medium text-content-secondary transition-colors hover:bg-surface-hover disabled:opacity-50"
                >
                    {loading ? (
                        <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    ) : (
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 12a9 9 0 11-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                            <path d="M21 3v5h-5" />
                        </svg>
                    )}
                    <span className="hidden sm:inline">{loading ? "갱신 중..." : "새로고침"}</span>
                </button>
            </div>
        );

        return (
            <div className="min-h-screen w-full overflow-x-hidden bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${collapsed ? "sm:pl-[56px]" : "sm:pl-[220px]"}`}
                    style={{ marginTop: "var(--maintenance-banner-h, 0px)" }}
                >
                    {/* 거래소 점검 중 배너 */}
                    <MaintenanceBanner />
                    {/* WebSocket 연결 끊김 시 폴링 모드 안내 토스트 배너 */}
                    <ToastBanner
                        visible={wsStatus === 'disconnected' && isPollingFallback}
                        variant="warning"
                        message="실시간 연결이 끊어졌습니다. 60초 간격으로 데이터를 갱신합니다."
                    />
                    {/* 전략 세그먼트 (DAS-175 Option A) — 최상위 축이 자산군에서 전략으로 바뀌었다.
                        모바일에서는 4개가 한 줄에 들어가지 않으므로 가로 스크롤한다. */}
                    <div className="mx-3 mt-3 flex flex-col gap-2 sm:mx-6 sm:mt-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <div
                            className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0"
                            role="tablist"
                            aria-label="대시보드 전략 뷰"
                        >
                            {[
                                { mode: 'all', label: '전체' },
                                { mode: 'btc', label: 'BTC 적립' },
                                { mode: 'delta', label: '델타전략' },
                                { mode: 'stock', label: '해외주식 적립' },
                            ].map(({ mode, label }) => (
                                <FilterPill
                                    key={mode}
                                    active={viewMode === mode}
                                    onClick={() => this.setViewMode(mode)}
                                    variant="solid"
                                    size="md"
                                    className="min-h-[44px] shrink-0 whitespace-nowrap"
                                >
                                    {label}
                                </FilterPill>
                            ))}
                        </div>
                        {statusBar}
                    </div>
                    {viewMode === 'all' && (
                        <div className="mx-3 my-3 space-y-3 sm:mx-6 sm:my-5 sm:space-y-4">
                            {heroBlock('overview')}
                            {heroLoading ? (
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    {[...Array(3)].map((_, i) => (
                                        <Skeleton key={i} variant="card" height="150px" />
                                    ))}
                                </div>
                            ) : (
                                <StrategyOverviewCards
                                    btcSummary={btcSummary}
                                    delta={{ ...deltaMetrics, riskyBalance: derivedTotals.totalRiskyBalance }}
                                    stockSummary={stockSummary}
                                    onSelect={this.setViewMode}
                                />
                            )}
                            <StrategyServiceStatusBoard services={this.state.strategyServices} />
                        </div>
                    )}
                    {viewMode === 'btc' && (
                        <div className="mx-3 my-3 sm:mx-6 sm:my-5">
                            {heroLoading ? (
                                heroSkeleton
                            ) : (
                                <BtcDcaPanel
                                    summary={btcSummary}
                                    series={{
                                        points: this.state.btcSeriesPoints,
                                        loading: this.state.btcSeriesLoading,
                                        error: this.state.btcSeriesError,
                                        snapshotSince: this.state.btcSnapshotSince,
                                        onRetry: this.fetchBtcSeries,
                                    }}
                                    onGoDelta={() => this.setViewMode('delta')}
                                />
                            )}
                        </div>
                    )}
                    {viewMode === 'delta' && (
                    <div className="mx-3 my-3 min-w-0 overflow-hidden rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:mx-6 sm:my-5 sm:p-6 lg:min-w-[960px]">
                        {/* 델타 Hero — 보유 BTC를 뺀 운용액과 리밸런싱 카드 (DAS-175) */}
                        {heroBlock('delta')}

                        <div className="mt-4 sm:mt-6">
                            {(() => {
                                const strategyGroups = {};
                                sections.forEach((section) => {
                                    const sn = section.strategyName || "default";
                                    if (!strategyGroups[sn]) strategyGroups[sn] = [];
                                    strategyGroups[sn].push(section);
                                });
                                const strategyNames = Object.keys(strategyGroups).sort((a, b) => {
                                    const aTotal = strategyGroups[a].reduce((sum, s) => sum + s.totalBalance, 0);
                                    const bTotal = strategyGroups[b].reduce((sum, s) => sum + s.totalBalance, 0);
                                    return bTotal - aTotal;
                                });
                                const activeStrategy = strategyNames.includes(this.state.activeStrategyTab)
                                    ? this.state.activeStrategyTab : strategyNames[0];
                                const activeStrategySections = strategyGroups[activeStrategy] || [];

                                return (
                                    <>
                                        {strategyNames.length > 1 && (
                                            <div className="flex gap-2 overflow-x-auto border-b border-surface-border pb-2">
                                                {strategyNames.map((strategyName) => {
                                                    const isActive = strategyName === activeStrategy;
                                                    const strategyTotal = strategyGroups[strategyName].reduce((sum, s) => sum + s.totalBalance, 0);
                                                    return (
                                                        <FilterPill
                                                            key={strategyName}
                                                            active={isActive}
                                                            onClick={() => this.setState({ activeStrategyTab: strategyName })}
                                                            variant="solid"
                                                            size="md"
                                                            className="whitespace-nowrap gap-2"
                                                        >
                                                            <span>{strategyName}</span>
                                                            <span className={`font-num text-xs ${isActive ? "text-white/70" : "text-content-muted"}`}>
                                                                ₩{Number(strategyTotal).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                            </span>
                                                        </FilterPill>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <div className={`space-y-4 sm:space-y-6 ${strategyNames.length > 1 ? "mt-4" : ""}`}>
                                            {activeStrategySections.length > 1 && (() => {
                                                const anyCollapsed = activeStrategySections.some((s, i) =>
                                                    collapsedSections[s.key] !== undefined ? collapsedSections[s.key] : i > 0
                                                );
                                                return (
                                                    <div className="flex justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const next = {};
                                                                activeStrategySections.forEach((s) => {
                                                                    next[s.key] = !anyCollapsed;
                                                                });
                                                                this.setState((prev) => ({
                                                                    collapsedSections: { ...prev.collapsedSections, ...next },
                                                                }));
                                                            }}
                                                            className="rounded-lg border border-surface-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-hover transition-colors"
                                                        >
                                                            {anyCollapsed ? "모두 펼치기" : "모두 접기"}
                                                        </button>
                                                    </div>
                                                );
                                            })()}
                                            {activeStrategySections.map((section, sectionIndex) => {
                                                const isCollapsed = collapsedSections[section.key] !== undefined
                                                    ? collapsedSections[section.key]
                                                    : sectionIndex > 0;
                                                return (
                                                    <div
                                                        key={section.key}
                                                        className="overflow-hidden rounded-2xl border border-surface-border bg-surface-elevated shadow-card"
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                this.setState((prev) => ({
                                                                    collapsedSections: {
                                                                        ...prev.collapsedSections,
                                                                        [section.key]: !isCollapsed,
                                                                    },
                                                                }))
                                                            }
                                                            className="flex w-full items-center justify-between px-4 py-3 sm:px-5 sm:py-4"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <span className="rounded-lg bg-brand/20 border border-brand/30 px-2.5 py-1 text-xs font-bold text-brand-light sm:text-sm">
                                                                    {section.label}
                                                                </span>
                                                                <span className="font-num text-xs text-content-secondary sm:text-sm">
                                                                    {section.hasBalance
                                                                        ? `₩${Number(section.totalBalance).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                                                                        : "—"}
                                                                </span>
                                                                {/* 잔고 조회 실패 시 직전 값 유지 가시화 (DAS-181) */}
                                                                {section.fetchBadge && (
                                                                    <span
                                                                        role="status"
                                                                        title={
                                                                            section.fetchErrors.length > 0
                                                                                ? section.fetchErrors.join(" / ")
                                                                                : "조회 지연 — 직전 값을 표시하고 있습니다"
                                                                        }
                                                                        aria-label={section.fetchBadge}
                                                                        className="rounded-full bg-warning-subtle px-2 py-0.5 text-xs font-semibold text-warning"
                                                                    >
                                                                        {section.fetchBadge}
                                                                    </span>
                                                                )}
                                                                {/* DAS-182: 코인빌리기(담보·대여) 잔차 추정 반영 중임을 카드 헤더에서 표시 */}
                                                                {section.loan && (
                                                                    <span
                                                                        title="코인빌리기(담보·대여) 잔차 추정이 잔고에 반영되고 있습니다"
                                                                        aria-label="코인빌리기 추정 반영중"
                                                                        className="rounded-full bg-brand-subtle px-2 py-0.5 text-xs font-semibold text-brand-light"
                                                                    >
                                                                        코인빌리기
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <svg
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                className={`h-4 w-4 text-content-muted transition-transform duration-300 ${isCollapsed ? "" : "rotate-180"}`}
                                                            >
                                                                <path d="M6 9l6 6 6-6" />
                                                            </svg>
                                                        </button>
                                                        <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isCollapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'}`}>
                                                            <div className="min-h-0 overflow-hidden">
                                                            <div className="space-y-4 px-4 pb-4 sm:space-y-6 sm:px-5 sm:pb-5">
                                                                <TotalBalanceExchangeRateBase
                                                                    fiatTotalBalance={section.fiatTotalBalance}
                                                                    fiatTotalUnrealizedProfit={section.fiatTotalUnrealizedProfit}
                                                                    riskyTotalMarginBalance={section.riskyTotalMarginBalance}
                                                                    riskyTotalUnrealizedProfit={section.riskyTotalUnrealizedProfit}
                                                                    btcSpotBalance={section.btcSpotBalance}
                                                                    exchangeRate={exchangeRate}
                                                                />
                                                                <PositionTable
                                                                    exchangeRate={exchangeRate}
                                                                    exchangeRateSource={this.state.exchangeRateSource}
                                                                    riskyPositions={section.riskyPositions}
                                                                    fiatPositions={section.fiatPositions}
                                                                    bollinger={currentRatio[section.indexKey]}
                                                                    riskyMarkPrices={section.markPrices}
                                                                    strategyName={section.strategyName}
                                                                    excludeBTC={excludeBTC}
                                                                    onToggleExcludeBTC={this.toggleExcludeBTC}
                                                                    loan={section.loan}
                                                                    username={getStoredUsername()}
                                                                    onLoanChanged={this.fetchData}
                                                                />
                                                            </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>                                        )}
                                        <div className="mt-4 sm:mt-6">
                                            <StrategyServiceStatusBoard services={this.state.strategyServices} />
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        {/* History 에서 옮겨온 부분 (DAS-179) — 리밸런싱·밴드로 '지금'을 보고 여기서 '지나온 길'을 본다.
                            BTC 적립분은 스냅샷이 있는 날만 뺄 수 있어 차트가 그 경계를 스스로 밝힌다. */}
                        <div className="mt-4 space-y-4 sm:mt-6">
                            <DeltaHistoryChart
                                series={deltaSeries}
                                loading={this.state.deltaHistoryLoading}
                                error={this.state.deltaHistoryError}
                                onRetry={this.fetchDeltaHistory}
                            />
                            {!this.state.deltaHistoryLoading && !this.state.deltaHistoryError && (
                                <>
                                    <DeltaMonthlySummary rows={deltaMonthly} />
                                    <HistoryTable balanceHistory={deltaTableRows} />
                                </>
                            )}
                        </div>
                    </div>
                    )}
                    {viewMode === 'stock' && (
                    <div className="mx-3 my-3 min-w-0 space-y-4 sm:mx-6 sm:my-5">
                        {stockTabEmpty ? (
                            <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                                <EmptyState
                                    title="아직 기록된 매수가 없습니다."
                                    description="증권사 앱에서 체결 후 수동 매수 추가로 등록하면 이곳에 표시됩니다."
                                    action={{ label: "수동 매수 추가", onClick: this.openAddStockPurchaseModal }}
                                />
                            </div>
                        ) : (
                            <>
                                <StockSummaryHero
                                    summary={stockSummary}
                                    loading={!stockSummary && !stockSummaryError}
                                    error={stockSummaryColdError}
                                    onRetry={this.fetchStockSummary}
                                />
                                <AccountCards
                                    accounts={stockSummary?.accounts}
                                    selectedAccountId={selectedAccountId}
                                    onSelect={this.onSelectAccount}
                                    onRenamed={this.handleAccountRenamed}
                                />
                                <AccumulationChart
                                    points={stockSeriesPoints}
                                    loading={stockSeriesLoading}
                                    error={stockSeriesError}
                                    onRetry={() => this.fetchStockSeries(selectedAccountId)}
                                    datasetKey={selectedAccountId || 0}
                                    snapshotSince={stockSeriesSnapshotSince}
                                    accountFallback={stockSeriesAccountFallback}
                                />
                                <FxBreakdownStrip
                                    summary={stockSummary}
                                    loading={!stockSummary && !stockSummaryError}
                                    error={stockSummaryColdError}
                                    onRetry={this.fetchStockSummary}
                                />
                                {showAccountChips && (
                                    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="계좌 필터">
                                        <FilterPill
                                            active={!selectedAccountId}
                                            onClick={() => this.onSelectAccount(null)}
                                            variant="solid"
                                        >
                                            전체
                                        </FilterPill>
                                        {holdingAccounts.map((a) => (
                                            <FilterPill
                                                key={a.account_id}
                                                active={selectedAccountId === a.account_id}
                                                onClick={() =>
                                                    this.onSelectAccount(selectedAccountId === a.account_id ? null : a.account_id)
                                                }
                                                variant="solid"
                                            >
                                                {a.label}
                                            </FilterPill>
                                        ))}
                                    </div>
                                )}
                                <PortfolioAllocation
                                    holdings={filteredStockHoldings}
                                    loading={stockHoldingsLoading}
                                    error={stockHoldingsError}
                                    onRetry={this.fetchStockHoldings}
                                />
                                <HoldingsTable
                                    holdings={filteredStockHoldings}
                                    loading={stockHoldingsLoading}
                                    error={stockHoldingsError}
                                    onRetry={this.fetchStockHoldings}
                                />
                                <PurchaseTimeline
                                    purchases={stockPurchases}
                                    loading={stockPurchasesLoading}
                                    error={stockPurchasesError}
                                    onRetry={this.fetchStockPurchases}
                                    onConfirm={this.handleConfirmStockPurchase}
                                    onEdit={this.openEditStockPurchaseModal}
                                    onDelete={this.handleDeleteStockPurchase}
                                    headerAction={
                                        <button
                                            type="button"
                                            onClick={this.openAddStockPurchaseModal}
                                            className="min-h-[44px] rounded-lg border border-brand/30 bg-brand/10 px-3 text-xs font-medium text-brand-light hover:bg-brand/20"
                                        >
                                            수동 매수 추가
                                        </button>
                                    }
                                />
                            </>
                        )}
                    </div>
                    )}
                </main>
                <ScrollToTopButton />
                <PurchaseFormModal
                    open={stockPurchaseModalOpen}
                    mode={stockPurchaseModalMode}
                    initial={stockPurchaseEditTarget}
                    onClose={this.closeStockPurchaseModal}
                    onSubmit={this.handleStockPurchaseSubmit}
                    saving={stockPurchaseSaving}
                />
            </div>
        );
    }
}

// DAS-171 F-8: dashboard_page_v2/v3(2700줄, VERSION_LOCKED 도달 불가)는 삭제됐다.
// VariantGate 자체는 다른 페이지(my_page 등)가 계속 쓰므로 유지 — v2/v3 슬롯만 비워 항상 v1로 폴백한다.
const DashboardPageV1 = DashboardPage;
export { DashboardPageV1 };
const DashboardPageWithVariant = (props) => (
    <VariantGate v1={DashboardPageV1} fallbackToV1={true} {...props} />
);
export default DashboardPageWithVariant;
